// src/components/public_utilities/AssetFormModal.js
// --- UPDATE (v2.0 AI Asset Helper)
//   • AI Asset Helper (compact) above the tabs: live AI-readiness score, which AI features
//     the asset will support, and a checklist of what is still missing — "Add" switches to
//     the right tab and focuses the field. Tab badges show missing required/recommended items.
//   • (?) explainers on every field: what it is, why FlowSense needs it, an example.
//   • Header "ML Readiness" now uses the AI guide's weighted score (was 6 fields, and an
//     elevation of 0 m counted as missing).
//   • FIX: isInvalid was inverted (`touched && !error`) — valid fields turned red and
//     invalid ones did not. Now `touched && error`.
//   • FIX: latitude / longitude / elevation of 0 were shown as empty (`value || ''`).
//   • useColorModeValue hoisted out of JSX (hooks must not be called inside markup).
// --- END UPDATE
// --- UPDATE (Public Utilities v3)
//   • Managed by: the organisation responsible (MOW+ internal team, contractor company, independent
//     contractor, BPL, BTC, ALIV, agency…) from /public-utilities/managers → managed_by_org_id.
//   • Constituency (= zone): "Automatic from GPS" by default; the form shows live where the point
//     falls ("In Bamboo Town") as coordinates are typed, or a constituency can be chosen.
//   • Asset code: shown read-only when editing (DW-BAM-0001); new assets get one on save in the same
//     format as roads (prefix-constituency-sequence).
//   • Duplicate guard (AI): when coordinates are entered, existing Public Utilities assets within
//     15 m are listed so the same well / basin is not registered twice.
//   • Building, Sidewalk and Drainage Well are always offered as types; statuses include
//     under maintenance, planned and closed (match the backend ENUM).
//   • FIX: an empty capital cost was saved as $0.00 — it is saved as empty (null) now.
// --- END UPDATE
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, FormControl, FormLabel, Input, Select, Textarea, VStack, HStack,
    useToast, Alert, AlertIcon, Badge, Box, Text, NumberInput, NumberInputField, 
    NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Tooltip, 
    IconButton, InputGroup, InputLeftElement, InputRightElement, Divider, Collapse, 
    useDisclosure, Tabs, TabList, TabPanels, Tab, TabPanel, Grid, GridItem, Spinner, useColorModeValue, Icon,
    Heading, SimpleGrid
} from '@chakra-ui/react';
import { InfoIcon, CheckIcon, AddIcon } from '@chakra-ui/icons';
import { 
    MdLocationOn, MdAttachMoney, MdCalendarToday, MdCategory, 
    MdQrCode, MdSettings, MdBuild, MdInfoOutline, MdHeight, MdWaves
} from 'react-icons/md';

import api from '../../services/api';
// --- UPDATE (v3)
import { listManagers as puListManagers, getZones as puGetZones, locateZone as puLocateZone, findNearbyAssets as puFindNearby } from '../../services/publicUtilitiesApi';
// --- END UPDATE
// --- UPDATE
import AiAssetHelper, { AssetFieldHint } from './AiAssetHelper';
import { analyzeAsset, fieldLocation } from './assetAiGuide';
// --- END UPDATE

// ============================================================================
// DEBUG UTILITY
// ============================================================================
const debugLog = (action, data, level = 'info') => {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, component: 'AssetFormModal', action, ...data };
    const styles = { info: 'color: #3182ce', success: 'color: #38a169', warning: 'color: #d69e2e', error: 'color: #e53e3e' };

    if (process.env.NODE_ENV === 'development' || window.__DEBUG__) {
        console.log(`%c[${timestamp}] [AssetFormModal] ${action}`, styles[level], data);
    }
    try {
        const logs = JSON.parse(sessionStorage.getItem('asset_form_logs') || '[]');
        logs.push(logEntry);
        if (logs.length > 50) logs.shift();
        sessionStorage.setItem('asset_form_logs', JSON.stringify(logs));
    } catch (e) {
        console.error('Failed to store log:', e);
    }
};

// ============================================================================
// CONSTANTS & SCHEMA MAPPINGS
// ============================================================================
const ASSET_STATUS = [
    'operational', 'maintenance_required', 'damaged', 'decommissioned', 'unknown', 'failed',
    'under_maintenance', 'planned', 'closed' // --- UPDATE (v3): backend ENUM
];

const CRITICALITY_LEVELS = [
    'low', 'medium', 'high', 'critical'
];

const STATUS_COLORS = {
    operational: 'green',
    maintenance_required: 'yellow',
    damaged: 'orange',
    decommissioned: 'gray',
    unknown: 'gray',
    failed: 'red',
    under_maintenance: 'blue', planned: 'cyan', closed: 'gray' // --- UPDATE (v3)
};

// ============================================================================
// VALIDATION ENGINE
// ============================================================================
const validators = {
    name: (value) => {
        if (!value || !value.trim()) return 'Asset name is required';
        if (value.length > 255) return 'Name must not exceed 255 characters';
        return null;
    },
    type: (value) => {
        if (!value || !value.trim()) return 'Asset type is required';
        return null;
    },
    location_description: (value) => {
        if (value && value.length > 255) return 'Location must not exceed 255 characters';
        return null;
    },
    latitude: (value) => {
        if (!value && value !== 0) return null;
        const lat = parseFloat(value);
        if (isNaN(lat)) return 'Latitude must be a valid number';
        if (lat < -90 || lat > 90) return 'Latitude must be between -90 and 90';
        return null;
    },
    longitude: (value) => {
        if (!value && value !== 0) return null;
        const lng = parseFloat(value);
        if (isNaN(lng)) return 'Longitude must be a valid number';
        if (lng < -180 || lng > 180) return 'Longitude must be between -180 and 180';
        return null;
    },
    elevation: (value) => {
        if (!value && value !== 0) return null;
        const el = parseFloat(value);
        if (isNaN(el)) return 'Elevation must be a valid numeric measurement';
        return null;
    },
    status: (value) => {
        if (!value) return 'Status is required';
        if (!ASSET_STATUS.includes(value)) return 'Invalid status value';
        return null;
    },
    purchase_cost: (value) => {
        if (value && parseFloat(value) < 0) return 'Purchase cost cannot be negative';
        return null;
    },
    expected_lifespan_years: (value) => {
        if (value && parseInt(value, 10) < 0) return 'Lifespan cannot be negative';
        return null;
    },
    asset_criticality: (value) => {
        if (value && !CRITICALITY_LEVELS.includes(value)) return 'Invalid criticality level';
        return null;
    }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function AssetFormModal({ isOpen, onClose, onSave, asset = null, userRole = 'admin' }) {
    const isEditing = !!asset;
    const toast = useToast();
    const { isOpen: showDebug, onToggle: toggleDebug } = useDisclosure();

    const bridgeSectionBg = useColorModeValue('gray.50', 'gray.750');
    const bridgeSectionBorder = useColorModeValue('teal.200', 'teal.700');
    const wellBenchmarkBg = useColorModeValue('teal.50', 'rgba(49, 151, 149, 0.12)');
    const wellBenchmarkBorder = useColorModeValue('teal.200', 'teal.600');
    const wellBenchmarkTextColor = useColorModeValue('teal.800', 'teal.200');
    // --- UPDATE: hoisted from JSX
    const headerBg = useColorModeValue('white', 'gray.800');
    const headerBorder = useColorModeValue('gray.100', 'gray.700');
    const bodyBg = useColorModeValue('gray.50', 'gray.900');
    const panelBg = useColorModeValue('white', 'gray.800');
    const newTypeBg = useColorModeValue('blue.50', 'blue.900');
    const footerBorder = useColorModeValue('gray.200', 'gray.700');
    const [activeTab, setActiveTab] = useState(0);
    // --- END UPDATE
    
    // Form State
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // Dynamic Asset Types State
    const [assetTypes, setAssetTypes] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    
    // New Asset Type Creation State
    const { isOpen: isCreatingType, onToggle: toggleCreatingType, onClose: closeCreatingType } = useDisclosure();
    const [newTypeData, setNewTypeData] = useState({ type_name: '', description: '', model_url: '' });
    const [isSavingType, setIsSavingType] = useState(false);

    // Teams State
    const [teams, setTeams] = useState([]);
    // --- UPDATE (v3): managers, constituencies, live placement + duplicate guard
    const [puManagers, setPuManagers] = useState([]);
    const [puZones, setPuZones] = useState([]);
    const [placement, setPlacement] = useState(null);   // { zone_name, constituency, method } | null
    const [nearby, setNearby] = useState([]);           // existing assets within 15 m
    const aiBoxBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.12)');
    // --- END UPDATE

    // ============================================================================
    // DATA INITIALIZATION & FETCHING
    // ============================================================================
    useEffect(() => {
        if (isOpen) {
            debugLog('modal:opened', { isEditing, asset: asset?.id });
            
            let parsedMetadata = {};
            try {
                parsedMetadata = typeof asset?.metadata === 'string'
                    ? JSON.parse(asset.metadata || '{}')
                    : (asset?.metadata || {});
            } catch (e) {
                parsedMetadata = {};
            }

            const initialData = {
                name: asset?.name || '',
                type: asset?.type || '',
                status: asset?.status || 'unknown',
                asset_criticality: asset?.asset_criticality || 'medium',
                location_description: asset?.location_description || '',
                latitude: asset?.latitude != null ? asset.latitude : '',
                longitude: asset?.longitude != null ? asset.longitude : '',
                elevation: asset?.elevation != null ? asset.elevation : '',
                manufacturer: asset?.manufacturer || '',
                model_number: asset?.model_number || '',
                serial_number: asset?.serial_number || '',
                install_date: asset?.install_date ? asset.install_date.split('T')[0] : '',
                warranty_expiration_date: asset?.warranty_expiration_date ? asset.warranty_expiration_date.split('T')[0] : '',
                last_maintenance_date: asset?.last_maintenance_date ? asset.last_maintenance_date.split('T')[0] : '',
                failure_date: asset?.failure_date ? asset.failure_date.split('T')[0] : '',
                expected_lifespan_years: asset?.expected_lifespan_years || 10,
                purchase_cost: asset?.purchase_cost || '',
                parent_asset_id: asset?.parent_asset_id || '',
                managed_by_team_id: asset?.managed_by_team_id || '',
                qr_code_tag: asset?.qr_code_tag || '',
                model_url: asset?.model_url || '',
                metadata: parsedMetadata,
                // --- UPDATE (v3)
                managed_by_org_id: asset?.managed_by_org_id != null ? String(asset.managed_by_org_id) : '',
                zone_id: asset?.zone_id != null ? String(asset.zone_id) : '',
                // --- END UPDATE
            };
            
            setFormData(initialData);
            setErrors({});
            setTouched({});
            setIsDirty(false);
            setActiveTab(0); // --- UPDATE
            closeCreatingType();
            
            fetchAssetTypes();
            fetchTeams();
            // --- UPDATE (v3): optional — the form works without the Public Utilities v3 backend
            setPlacement(null); setNearby([]);
            puListManagers().then((m) => setPuManagers(Array.isArray(m) ? m.filter((x) => x.is_active !== false || String(x.id) === String(asset?.managed_by_org_id)) : [])).catch(() => setPuManagers([]));
            puGetZones().then((z) => setPuZones(Array.isArray(z?.zones) ? z.zones : [])).catch(() => setPuZones([]));
            // --- END UPDATE
        }
    }, [isOpen, asset, isEditing]);

    // --- UPDATE (v3): where does the point fall, and is something already registered there?
    useEffect(() => {
        if (!isOpen) return undefined;
        const lat = parseFloat(formData.latitude); const lng = parseFloat(formData.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) { setPlacement(null); setNearby([]); return undefined; }
        let cancelled = false;
        const t = setTimeout(() => {
            puLocateZone(lat, lng).then((p) => { if (!cancelled) setPlacement(p && (p.zone_id != null || p.constituency) ? p : { none: true }); }).catch(() => { if (!cancelled) setPlacement(null); });
            puFindNearby(lat, lng, 15).then((list) => { if (!cancelled) setNearby((list || []).filter((x) => String(x.id) !== String(asset?.id))); }).catch(() => { if (!cancelled) setNearby([]); });
        }, 600);
        return () => { cancelled = true; clearTimeout(t); };
    }, [isOpen, formData.latitude, formData.longitude, asset?.id]);
    // --- END UPDATE

    const fetchAssetTypes = async () => {
        setIsLoadingTypes(true);
        try {
            const response = await api.get('/assets/types'); 
            const types = response.data?.data || response.data || [];
            
            const hasCatchBasin = types.some(t => t.type_name?.toLowerCase() === 'catch basin');
            if (!hasCatchBasin) {
                types.push({ id: 'virtual-cb', type_name: 'Catch Basin', description: 'Stormwater management infrastructure' });
            }
            // --- UPDATE (v3): Public Utilities catalogue types are always available
            [['Drainage Well', 'virtual-dw'], ['Building', 'virtual-bld'], ['Sidewalk', 'virtual-sw']].forEach(([name, id]) => {
                if (!types.some(t => String(t.type_name || '').toLowerCase() === name.toLowerCase())) types.push({ id, type_name: name });
            });
            // --- END UPDATE

            setAssetTypes(types);
            debugLog('fetch:asset_types', { count: types.length });
        } catch (error) {
            console.error("Failed to load asset types", error);
            setAssetTypes([
                { id: 1, type_name: 'Catch Basin' },
                { id: 2, type_name: 'Street Light' },
                { id: 3, type_name: 'Traffic Light' },
                { id: 4, type_name: 'Fire Hydrant' },
                { id: 5, type_name: 'Water Valve' },
                { id: 6, type_name: 'Pump Station' },
                { id: 7, type_name: 'Well' },
                { id: 8, type_name: 'Road' },
                // --- UPDATE (v3)
                { id: 9, type_name: 'Drainage Well' },
                { id: 10, type_name: 'Building' },
                { id: 11, type_name: 'Sidewalk' },
                // --- END UPDATE
            ]);
        } finally {
            setIsLoadingTypes(false);
        }
    };

    const fetchTeams = async () => {
        try {
            const response = await api.get('/teams');
            setTeams(response.data?.data || response.data || []);
        } catch (error) {
            debugLog('fetch:teams_failed', error.message, 'warning');
        }
    };

    // ============================================================================
    // NEW ASSET TYPE CREATION LOGIC
    // ============================================================================
    const handleSaveNewType = async () => {
        if (!newTypeData.type_name.trim()) {
            toast({ title: 'Error', description: 'Type name is required', status: 'error', duration: 3000 });
            return;
        }

        setIsSavingType(true);
        try {
            const response = await api.post('/assets/types', newTypeData);
            toast({
                title: 'Success',
                description: `Asset Type "${newTypeData.type_name}" created and saved to database.`,
                status: 'success',
                duration: 3000,
                isClosable: true
            });

            await fetchAssetTypes();
            handleChange('type', newTypeData.type_name);
            setNewTypeData({ type_name: '', description: '', model_url: '' });
            closeCreatingType();
        } catch (error) {
            toast({
                title: 'Failed to create asset type',
                description: error.response?.data?.message || error.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setIsSavingType(false);
        }
    };

    // ============================================================================
    // FORM VALIDATION & HANDLING
    // ============================================================================
    const validateField = useCallback((field, value) => {
        const validator = validators[field];
        if (validator) {
            return validator(value);
        }
        return null;
    }, []);

    const validateForm = useCallback(() => {
        const newErrors = {};
        Object.keys(validators).forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) newErrors[field] = error;
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, validateField]);

    const handleChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
        
        if (touched[field]) {
            const error = validateField(field, value);
            setErrors(prev => ({ ...prev, [field]: error }));
        }
    }, [touched, validateField]);

    const handleMetadataChange = useCallback((category, field, value) => {
        setFormData(prev => {
            const currentMeta = prev.metadata || {};
            if (field === null) {
                return {
                    ...prev,
                    metadata: {
                        ...currentMeta,
                        [category]: value
                    }
                };
            }
            return {
                ...prev,
                metadata: {
                    ...currentMeta,
                    [category]: {
                        ...(currentMeta[category] || {}),
                        [field]: value
                    }
                }
            };
        });
        setIsDirty(true);
    }, []);

    const handleBlur = useCallback((field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        const error = validateField(field, formData[field]);
        setErrors(prev => ({ ...prev, [field]: error }));
    }, [formData, validateField]);

    // ============================================================================
    // SUBMISSION LOGIC
    // ============================================================================
    const handleSubmit = async () => {
        debugLog('form:submit_started', { isEditing, assetId: asset?.id });
        
        const requiredFields = ['name', 'type', 'status'];
        const allTouched = { ...touched };
        requiredFields.forEach(k => allTouched[k] = true);
        setTouched(allTouched);

        if (!validateForm()) {
            toast({
                title: 'Validation Error',
                description: 'Please fix the highlighted errors before saving.',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const rawDepth = formData.metadata?.base_depth_m;
            const calibratedBaseDepth = (rawDepth !== undefined && rawDepth !== '')
                ? parseFloat(rawDepth)
                : (formData.elevation !== '' ? parseFloat(formData.elevation) : undefined);

            const submissionData = {
                ...formData,
                ...(isEditing && asset?.id != null ? { id: asset.id } : {}), // --- UPDATE (edit-only fix): edits carry their id
                latitude: formData.latitude !== '' ? parseFloat(formData.latitude) : null,
                longitude: formData.longitude !== '' ? parseFloat(formData.longitude) : null,
                elevation: formData.elevation !== '' ? parseFloat(formData.elevation) : null, // 🚀 Persisted Survey Elevation
                purchase_cost: formData.purchase_cost !== '' && formData.purchase_cost != null ? parseFloat(formData.purchase_cost) : null, // --- UPDATE (v3): was 0.00 (an invented cost)
                // --- UPDATE (v3): Public Utilities fields ('' = automatic / not set)
                managed_by_org_id: formData.managed_by_org_id !== '' && formData.managed_by_org_id != null ? Number(formData.managed_by_org_id) : null,
                zone_id: formData.zone_id !== '' && formData.zone_id != null ? formData.zone_id : undefined,
                constituency: formData.zone_id ? (puZones.find(z => String(z.id) === String(formData.zone_id))?.constituency || puZones.find(z => String(z.id) === String(formData.zone_id))?.name || undefined) : undefined,
                // --- END UPDATE
                expected_lifespan_years: formData.expected_lifespan_years !== '' ? parseInt(formData.expected_lifespan_years, 10) : 10,
                managed_by_team_id: formData.managed_by_team_id !== '' ? parseInt(formData.managed_by_team_id, 10) : null,
                install_date: formData.install_date || null,
                warranty_expiration_date: formData.warranty_expiration_date || null,
                last_maintenance_date: formData.last_maintenance_date || null,
                failure_date: formData.failure_date || null,
                parent_asset_id: formData.parent_asset_id || null,
                metadata: {
                    ...(formData.metadata || {}),
                    ...(calibratedBaseDepth !== undefined ? { base_depth_m: calibratedBaseDepth } : {})
                }
            };

            await onSave(submissionData, asset?.id);

            toast({
                title: isEditing ? 'Asset Updated' : 'Asset Created',
                description: `Asset "${submissionData.name}" saved to the database.`,
                status: 'success',
                duration: 3000,
                isClosable: true
            });

            onClose();
        } catch (error) {
            debugLog('form:submit_error', { error: error.message }, 'error');
            toast({
                title: 'Database Error',
                description: error.response?.data?.message || error.message || 'Failed to save asset to database.',
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = useCallback(() => {
        if (isDirty && !window.confirm('You have unsaved changes. Are you sure you want to discard them?')) {
            return;
        }
        onClose();
    }, [isDirty, onClose]);

    const renderFormError = (field) => {
        if (touched[field] && errors[field]) {
            return <Text color="red.500" fontSize="xs" mt={1}>{errors[field]}</Text>;
        }
        return null;
    };

    // --- UPDATE: weighted AI-readiness from the asset AI guide (0 m elevation counts as filled)
    const aiAnalysis = useMemo(() => analyzeAsset({ ...(asset || {}), ...formData }, 'form'), [asset, formData]);
    const formCompleteness = aiAnalysis.score;
    const missingByTab = useMemo(() => {
        const counts = {};
        aiAnalysis.checks.forEach(c => {
            if (c.status === 'ok' || c.level === 'optional' || !c.location) return;
            counts[c.location.tab] = (counts[c.location.tab] || 0) + 1;
        });
        return counts;
    }, [aiAnalysis]);
    const focusField = useCallback((fieldId) => {
        const loc = fieldLocation(fieldId, 'form');
        if (!loc) return;
        setActiveTab(loc.tab);
        setTimeout(() => {
            const el = document.getElementById(loc.domId);
            if (el) {
                el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                el.focus?.({ preventScroll: true });
            }
        }, 80);
    }, []);
    const tabBadge = (i) => (missingByTab[i] ? <Badge ml={2} colorScheme="orange" borderRadius="full" fontSize="9px">{missingByTab[i]}</Badge> : null);
    // --- END UPDATE

    const isHydraulicAsset = useMemo(() => {
        const t = (formData.type || '').toLowerCase();
        return t.includes('well') || t.includes('basin') || t.includes('pump') || t.includes('hydrant');
    }, [formData.type]);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="4xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(3px)" bg="blackAlpha.300" />
            <ModalContent borderRadius="xl" overflow="hidden">
                
                {/* MODAL HEADER */}
                <ModalHeader bg={headerBg} borderBottomWidth="1px" borderColor={headerBorder}>
                    <HStack justify="space-between">
                        <Box>
                            <Text fontWeight="800" fontSize="xl">
                                {isEditing ? 'Edit Enterprise Asset' : 'Register New Asset'}
                            </Text>
                            <Text fontSize="sm" color="gray.500" fontWeight="normal" fontFamily="mono">
                                {isEditing && asset?.id ? `ID: ${asset.id}` : 'FlowSense Infrastructure Database'}
                            </Text>
                        </Box>
                        <HStack spacing={3}>
                            {isDirty && <Badge colorScheme="orange">Unsaved Changes</Badge>}
                            {/* --- UPDATE: AI guide score + explanation */}
                            <Tooltip hasArrow label="Weighted share of the information FlowSense's AI uses for this asset type. See the AI Asset Helper below for what is missing and why.">
                                <Badge colorScheme={aiAnalysis.grade.color} cursor="help">
                                    AI Readiness: {formCompleteness}%
                                </Badge>
                            </Tooltip>
                            {/* --- END UPDATE */}
                            <Tooltip label="Toggle Schema Debugger">
                                <IconButton icon={<InfoIcon />} size="sm" variant="ghost" onClick={toggleDebug} aria-label="Debug" />
                            </Tooltip>
                        </HStack>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton mt={2} />

                {/* MODAL BODY */}
                <ModalBody p={0} bg={bodyBg}>
                    <Collapse in={showDebug}>
                        <Alert status="info" fontSize="xs" borderRadius="none">
                            <AlertIcon />
                            <Box flex={1}>
                                <Text fontWeight="bold">Database Schema Mapping Info</Text>
                                <Text>Errors: {JSON.stringify(errors)}</Text>
                                <Text>Target Table: `mow_backend.assets`</Text>
                            </Box>
                        </Alert>
                    </Collapse>

                    {/* --- UPDATE: AI Asset Helper */}
                    <Box px={4} pt={4}>
                        <AiAssetHelper asset={{ ...(asset || {}), ...formData }} analysis={aiAnalysis} context="form" variant="compact"
                            onFix={(fieldId) => focusField(fieldId)} defaultOpen={false} />
                    </Box>
                    {/* --- END UPDATE */}
                    <Tabs colorScheme="blue" variant="enclosed" p={4} /* --- UPDATE */ index={activeTab} onChange={setActiveTab} /* --- END UPDATE */>
                        <TabList mb={4}>
                            <Tab><Icon as={MdInfoOutline} mr={2} /> Core Identity{tabBadge(0)}</Tab>
                            <Tab><Icon as={MdLocationOn} mr={2} /> Geography & Elevation{tabBadge(1)}</Tab>
                            <Tab><Icon as={MdBuild} mr={2} /> Hardware & Lifecycle{tabBadge(2)}</Tab>
                            <Tab><Icon as={MdSettings} mr={2} /> System Specs{tabBadge(3)}</Tab>
                        </TabList>

                        <TabPanels>
                            
                            {/* TAB 1: CORE IDENTITY */}
                            <TabPanel bg={panelBg} borderRadius="md" shadow="sm" borderWidth="1px">
                                <VStack spacing={5} align="stretch">
                                    <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                                        <GridItem colSpan={2}>
                                            <FormControl id="asset-field-name" isRequired isInvalid={!!(touched.name && errors.name)}>
                                                <FormLabel fontSize="sm" fontWeight="bold">Asset Name / Tag<AssetFieldHint field="name" /></FormLabel>
                                                <Input 
                                                    value={formData.name || ''} 
                                                    onChange={(e) => handleChange('name', e.target.value)}
                                                    onBlur={() => handleBlur('name')}
                                                    placeholder="e.g., Well Station #04 - South Road" 
                                                />
                                                {renderFormError('name')}
                                                <Text fontSize="xs" color="gray.500" mt={1}>Human-readable identifier. Corresponds to `name` in DB.</Text>
                                            </FormControl>
                                        </GridItem>

                                        <GridItem colSpan={1}>
                                            <FormControl id="asset-field-type" isRequired isInvalid={!!(touched.type && errors.type)}>
                                                <FormLabel fontSize="sm" fontWeight="bold">Asset Type Classification<AssetFieldHint field="type" /></FormLabel>
                                                <HStack>
                                                    <Select
                                                        value={formData.type || ''}
                                                        onChange={(e) => {
                                                            if (e.target.value === '__CREATE_NEW__') {
                                                                toggleCreatingType();
                                                            } else {
                                                                handleChange('type', e.target.value);
                                                            }
                                                        }}
                                                        onBlur={() => handleBlur('type')}
                                                        isDisabled={isLoadingTypes}
                                                    >
                                                        <option value="">Select infrastructure type</option>
                                                        {assetTypes.map(t => (
                                                            <option key={t.id || t.type_name} value={t.type_name}>{t.type_name}</option>
                                                        ))}
                                                        {userRole === 'admin' && (
                                                            <option value="__CREATE_NEW__" style={{ fontWeight: 'bold', color: 'blue' }}>
                                                                + Create New Type...
                                                            </option>
                                                        )}
                                                    </Select>
                                                    {isLoadingTypes && <Spinner size="sm" />}
                                                </HStack>
                                                {renderFormError('type')}
                                            </FormControl>
                                        </GridItem>

                                        <GridItem colSpan={1}>
                                            <FormControl id="asset-field-status" isRequired isInvalid={!!(touched.status && errors.status)}>
                                                <FormLabel fontSize="sm" fontWeight="bold">Current Status<AssetFieldHint field="status" /></FormLabel>
                                                <Select value={formData.status || 'unknown'} onChange={(e) => handleChange('status', e.target.value)}>
                                                    {ASSET_STATUS.map(status => (
                                                        <option key={status} value={status}>{status.replace(/_/g, ' ').toUpperCase()}</option>
                                                    ))}
                                                </Select>
                                                <Badge mt={2} colorScheme={STATUS_COLORS[formData.status] || 'gray'}>
                                                    {formData.status?.replace(/_/g, ' ').toUpperCase()}
                                                </Badge>
                                            </FormControl>
                                        </GridItem>
                                    </Grid>

                                    {/* DYNAMIC TYPE CREATION WIDGET */}
                                    <Collapse in={isCreatingType}>
                                        <Box p={4} bg={newTypeBg} borderRadius="md" borderWidth="1px" borderColor="blue.200">
                                            <Text fontWeight="bold" mb={3} fontSize="sm" color="blue.600">
                                                <Icon as={MdCategory} mr={2} /> Register New Global Asset Type
                                            </Text>
                                            <Grid templateColumns="repeat(12, 1fr)" gap={3}>
                                                <GridItem colSpan={5}>
                                                    <Input 
                                                        size="sm" 
                                                        placeholder="Type Name (e.g., Catch Basin)" 
                                                        value={newTypeData.type_name}
                                                        onChange={(e) => setNewTypeData({...newTypeData, type_name: e.target.value})}
                                                    />
                                                </GridItem>
                                                <GridItem colSpan={7}>
                                                    <Input 
                                                        size="sm" 
                                                        placeholder="Description (Optional)" 
                                                        value={newTypeData.description}
                                                        onChange={(e) => setNewTypeData({...newTypeData, description: e.target.value})}
                                                    />
                                                </GridItem>
                                            </Grid>
                                            <HStack mt={3} justify="flex-end">
                                                <Button size="xs" variant="ghost" onClick={closeCreatingType}>Cancel</Button>
                                                <Button size="xs" colorScheme="blue" leftIcon={<AddIcon />} isLoading={isSavingType} onClick={handleSaveNewType}>
                                                    Save Type to Database
                                                </Button>
                                            </HStack>
                                        </Box>
                                    </Collapse>

                                    <Divider />

                                    <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                                        <GridItem colSpan={1}>
                                            <FormControl id="asset-field-asset_criticality">
                                                <FormLabel fontSize="sm" fontWeight="bold">Asset Criticality (ML Weighting)<AssetFieldHint field="asset_criticality" /></FormLabel>
                                                <Select value={formData.asset_criticality || 'medium'} onChange={(e) => handleChange('asset_criticality', e.target.value)}>
                                                    {CRITICALITY_LEVELS.map(crit => (
                                                        <option key={crit} value={crit}>{crit.toUpperCase()}</option>
                                                    ))}
                                                </Select>
                                                <Text fontSize="xs" color="gray.500" mt={1}>Used by AI engine to prioritize alerts.</Text>
                                            </FormControl>
                                        </GridItem>

                                        {/* --- UPDATE (v3): organisation responsible + asset code */}
                                        <GridItem colSpan={1}>
                                            <FormControl id="asset-field-managed_by_org_id">
                                                <FormLabel fontSize="sm" fontWeight="bold">Managed By (organisation)</FormLabel>
                                                <Select placeholder="Not assigned yet" value={formData.managed_by_org_id || ''} onChange={(e) => handleChange('managed_by_org_id', e.target.value)}
                                                    isDisabled={!puManagers.length} aria-label="Managed by organisation">
                                                    {puManagers.map(m => (
                                                        <option key={m.id} value={String(m.id)}>{m.name}{m.kind_label ? ` — ${m.kind_label}` : ''}{m.is_active === false ? ' (inactive)' : ''}</option>
                                                    ))}
                                                </Select>
                                                <Text fontSize="xs" color="gray.500" mt={1}>
                                                    {puManagers.length ? 'MOW+ team, contractor company, independent contractor, BPL, BTC, ALIV…' : 'Add organisations on Public Utilities → Managers.'}
                                                </Text>
                                            </FormControl>
                                        </GridItem>
                                        <GridItem colSpan={1}>
                                            <FormControl id="asset-field-asset_code">
                                                <FormLabel fontSize="sm" fontWeight="bold">Asset Code</FormLabel>
                                                <Input value={asset?.asset_code || ''} isReadOnly placeholder="Assigned on save, e.g. BLD-BAM-0001" fontFamily="mono" />
                                                <Text fontSize="xs" color="gray.500" mt={1}>Same format as road IDs — type prefix, constituency, number. It never changes.</Text>
                                            </FormControl>
                                        </GridItem>
                                        {/* --- END UPDATE */}
                                        <GridItem colSpan={1}>
                                            <FormControl id="asset-field-managed_by_team_id">
                                                <FormLabel fontSize="sm" fontWeight="bold">Managed By Team<AssetFieldHint field="managed_by_team_id" /></FormLabel>
                                                <Select 
                                                    placeholder="Unassigned (Global)" 
                                                    value={formData.managed_by_team_id || ''} 
                                                    onChange={(e) => handleChange('managed_by_team_id', e.target.value)}
                                                >
                                                    {teams.map(team => (
                                                        <option key={team.id} value={team.id}>{team.name}</option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </GridItem>
                                    </Grid>
                                </VStack>
                            </TabPanel>

                            {/* TAB 2: GEOGRAPHY & ELEVATION */}
                            <TabPanel bg={panelBg} borderRadius="md" shadow="sm" borderWidth="1px">
                                <VStack spacing={5} align="stretch">
                                    <FormControl id="asset-field-location_description">
                                        <FormLabel fontSize="sm" fontWeight="bold">Geographic Location Description<AssetFieldHint field="location_description" /></FormLabel>
                                        <Textarea 
                                            value={formData.location_description || ''} 
                                            onChange={(e) => handleChange('location_description', e.target.value)}
                                            placeholder="Physical landmark or road intersection..."
                                            rows={2}
                                        />
                                        <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                                            {formData.location_description?.length || 0}/255
                                        </Text>
                                    </FormControl>

                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                        <FormControl id="asset-field-latitude" isInvalid={!!(touched.latitude && errors.latitude)}>
                                            <FormLabel fontSize="sm" fontWeight="bold">Latitude<AssetFieldHint field="coordinates" /></FormLabel>
                                            <NumberInput value={formData.latitude ?? ''} onBlur={() => handleBlur('latitude')} onChange={(val) => handleChange('latitude', val)} precision={8}>
                                                <NumberInputField placeholder="25.0343" />
                                            </NumberInput>
                                            {renderFormError('latitude')}
                                        </FormControl>

                                        <FormControl id="asset-field-longitude" isInvalid={!!(touched.longitude && errors.longitude)}>
                                            <FormLabel fontSize="sm" fontWeight="bold">Longitude</FormLabel>
                                            <NumberInput value={formData.longitude ?? ''} onBlur={() => handleBlur('longitude')} onChange={(val) => handleChange('longitude', val)} precision={8}>
                                                <NumberInputField placeholder="-77.3963" />
                                            </NumberInput>
                                            {renderFormError('longitude')}
                                        </FormControl>

                                        <FormControl id="asset-field-elevation" isInvalid={!!(touched.elevation && errors.elevation)}>
                                            <Tooltip label="Surveyed height relative to Mean Sea Level (MSL) datum. Critical for runoff gradient calculations." hasArrow>
                                                <FormLabel fontSize="sm" fontWeight="bold" cursor="help">
                                                    Surveyed Elevation (m)<AssetFieldHint field="elevation" />
                                                </FormLabel>
                                            </Tooltip>
                                            <InputGroup>
                                                <NumberInput value={formData.elevation ?? ''} onChange={(val) => handleChange('elevation', val)} onBlur={() => handleBlur('elevation')} precision={2} w="full">
                                                    <NumberInputField placeholder="e.g. 4.25" />
                                                </NumberInput>
                                                <InputRightElement color="gray.500" fontSize="xs">m</InputRightElement>
                                            </InputGroup>
                                            {renderFormError('elevation')}
                                            <Text fontSize="2xs" color="gray.500" mt={1}>Direct MSL elevation for hydraulic AI head delta.</Text>
                                        </FormControl>
                                    </SimpleGrid>

                                    {/* --- UPDATE (v3): constituency (= zone) + AI placement + duplicate guard */}
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                        <FormControl id="asset-field-zone_id">
                                            <FormLabel fontSize="sm" fontWeight="bold">Constituency / Zone</FormLabel>
                                            <Select value={formData.zone_id || ''} onChange={(e) => handleChange('zone_id', e.target.value)} aria-label="Constituency or zone">
                                                <option value="">Automatic from GPS</option>
                                                {puZones.map(z => <option key={z.id} value={String(z.id)}>{z.constituency && z.constituency !== z.name ? `${z.constituency} (${z.name})` : z.name}</option>)}
                                            </Select>
                                            <Text fontSize="xs" color="gray.500" mt={1}>Zones and constituencies are the same thing in FlowSense.</Text>
                                        </FormControl>
                                        <Box p={3} borderRadius="lg" bg={aiBoxBg} aria-live="polite" data-testid="asset-form-placement">
                                            <Text fontSize="xs" fontWeight="800" mb={1}>AI placement check</Text>
                                            {!placement && <Text fontSize="xs" color="gray.500">Enter latitude and longitude to see which constituency this asset is in.</Text>}
                                            {placement && placement.none && <Text fontSize="xs" color="orange.500">This point is outside every drawn constituency boundary — choose one, or check the coordinates.</Text>}
                                            {placement && !placement.none && (
                                                <Text fontSize="xs">In <b>{placement.constituency || placement.zone_name}</b>{placement.method && placement.method !== 'gps' ? ` (matched by ${placement.method})` : ''}.</Text>
                                            )}
                                            {nearby.length > 0 && (
                                                <Box mt={2}>
                                                    <Text fontSize="xs" fontWeight="800" color="orange.500">Possible duplicate — {nearby.length} asset{nearby.length === 1 ? '' : 's'} already within 15 m:</Text>
                                                    {nearby.slice(0, 3).map(x => (
                                                        <Text key={x.id} fontSize="xs" fontFamily="mono">{x.code || String(x.id).slice(0, 8)} · {String(x.type || '').replace(/_/g, ' ')} · {x.name || '—'}</Text>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </SimpleGrid>
                                    {/* --- END UPDATE */}

                                    {/* HYDROLOGICAL BENCHMARKS (Wells, Basins, Outfalls) */}
                                    {(isHydraulicAsset || userRole === 'admin') && (
                                        <Box 
                                            p={4} 
                                            mt={2} 
                                            bg={wellBenchmarkBg} 
                                            borderRadius="lg" 
                                            borderWidth="1px" 
                                            borderColor={wellBenchmarkBorder}
                                        >
                                            <HStack mb={2} spacing={2}>
                                                <Icon as={MdWaves} color="teal.500" />
                                                <Text fontWeight="bold" fontSize="sm" color={wellBenchmarkTextColor}>
                                                    Hydrogeological Survey & Inundation Benchmarks
                                                </Text>
                                                <Badge colorScheme="teal" fontSize="9px">AI HYDRAULICS</Badge>
                                            </HStack>
                                            
                                            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} mt={3}>
                                                <FormControl id="asset-field-base_depth_m">
                                                    <FormLabel fontSize="xs" fontWeight="bold">Design / Sump Depth (Meters)<AssetFieldHint field="base_depth_m" /></FormLabel>
                                                    <NumberInput 
                                                        value={formData.metadata?.base_depth_m !== undefined ? formData.metadata.base_depth_m : ''} 
                                                        onChange={(val) => {
                                                            const numericVal = val === '' ? '' : parseFloat(val);
                                                            handleMetadataChange('base_depth_m', null, numericVal);
                                                        }} 
                                                        precision={2}
                                                        step={0.5}
                                                        min={0}
                                                    >
                                                        <NumberInputField placeholder="e.g. 145.0" />
                                                    </NumberInput>
                                                    <Text fontSize="2xs" color="gray.500" mt={1}>
                                                        Design depth used for well silt accumulation, storage volume, and head rise rate.
                                                    </Text>
                                                </FormControl>

                                                <FormControl id="asset-field-rim_elevation_offset_m">
                                                    <FormLabel fontSize="xs" fontWeight="bold">Curb / Rim Height Offset (Meters)<AssetFieldHint field="rim_elevation_offset_m" /></FormLabel>
                                                    <NumberInput 
                                                        value={formData.metadata?.rim_elevation_offset_m !== undefined ? formData.metadata.rim_elevation_offset_m : ''} 
                                                        onChange={(val) => {
                                                            const numericVal = val === '' ? '' : parseFloat(val);
                                                            handleMetadataChange('rim_elevation_offset_m', null, numericVal);
                                                        }} 
                                                        precision={2}
                                                        step={0.05}
                                                    >
                                                        <NumberInputField placeholder="e.g. 0.15" />
                                                    </NumberInput>
                                                    <Text fontSize="2xs" color="gray.500" mt={1}>
                                                        Well rim height above pavement grade to evaluate overtopping spill lines.
                                                    </Text>
                                                </FormControl>
                                            </Grid>
                                        </Box>
                                    )}
                                </VStack>
                            </TabPanel>

                            {/* TAB 3: HARDWARE & LIFECYCLE */}
                            <TabPanel bg={panelBg} borderRadius="md" shadow="sm" borderWidth="1px">
                                <VStack spacing={5} align="stretch">
                                    <SimpleGrid columns={3} gap={4}>
                                        <FormControl id="asset-field-manufacturer">
                                            <FormLabel fontSize="sm" fontWeight="bold">Manufacturer<AssetFieldHint field="manufacturer" /></FormLabel>
                                            <Input value={formData.manufacturer || ''} onChange={(e) => handleChange('manufacturer', e.target.value)} placeholder="OEM Name" />
                                        </FormControl>
                                        <FormControl id="asset-field-model_number">
                                            <FormLabel fontSize="sm" fontWeight="bold">Model Number<AssetFieldHint field="model_number" /></FormLabel>
                                            <Input value={formData.model_number || ''} onChange={(e) => handleChange('model_number', e.target.value)} placeholder="Model Tag" />
                                        </FormControl>
                                        <FormControl id="asset-field-serial_number">
                                            <FormLabel fontSize="sm" fontWeight="bold">Serial Number<AssetFieldHint field="serial_number" /></FormLabel>
                                            <Input value={formData.serial_number || ''} onChange={(e) => handleChange('serial_number', e.target.value)} placeholder="Unique S/N" />
                                        </FormControl>
                                    </SimpleGrid>

                                    <Divider />

                                    <SimpleGrid columns={3} gap={4}>
                                        <FormControl id="asset-field-install_date">
                                            <FormLabel fontSize="sm" fontWeight="bold">Install Date<AssetFieldHint field="install_date" /></FormLabel>
                                            <Input type="date" value={formData.install_date || ''} onChange={(e) => handleChange('install_date', e.target.value)} />
                                        </FormControl>
                                        <FormControl id="asset-field-warranty_expiration_date">
                                            <FormLabel fontSize="sm" fontWeight="bold">Warranty Expiration<AssetFieldHint field="warranty_expiration_date" /></FormLabel>
                                            <Input type="date" value={formData.warranty_expiration_date || ''} onChange={(e) => handleChange('warranty_expiration_date', e.target.value)} />
                                        </FormControl>
                                        <FormControl id="asset-field-expected_lifespan_years" isInvalid={!!(touched.expected_lifespan_years && errors.expected_lifespan_years)}>
                                            <FormLabel fontSize="sm" fontWeight="bold">Design Lifespan (Years)<AssetFieldHint field="expected_lifespan_years" /></FormLabel>
                                            <NumberInput value={formData.expected_lifespan_years || 10} onChange={(val) => handleChange('expected_lifespan_years', val)} min={1}>
                                                <NumberInputField />
                                            </NumberInput>
                                            {renderFormError('expected_lifespan_years')}
                                        </FormControl>
                                    </SimpleGrid>

                                    <FormControl id="asset-field-purchase_cost" isInvalid={!!(touched.purchase_cost && errors.purchase_cost)}>
                                        <FormLabel fontSize="sm" fontWeight="bold">Capital Cost ($)<AssetFieldHint field="purchase_cost" /></FormLabel>
                                        <InputGroup>
                                            <InputLeftElement><MdAttachMoney color="gray" /></InputLeftElement>
                                            <NumberInput value={formData.purchase_cost || ''} onChange={(val) => handleChange('purchase_cost', val)} precision={2} w="full" min={0}>
                                                <NumberInputField pl={10} placeholder="0.00" />
                                            </NumberInput>
                                        </InputGroup>
                                        {renderFormError('purchase_cost')}
                                        <Text fontSize="2xs" color="gray.500" mt={1}>Required for automated Repair vs. Replace Lifecycle ML.</Text>
                                    </FormControl>
                                </VStack>
                            </TabPanel>

                            {/* TAB 4: SYSTEM SPECS */}
                            <TabPanel bg={panelBg} borderRadius="md" shadow="sm" borderWidth="1px">
                                <VStack spacing={5} align="stretch">
                                    <FormControl id="asset-field-parent_asset_id">
                                        <FormLabel fontSize="sm" fontWeight="bold">Parent Facility / Asset ID<AssetFieldHint field="parent_asset_id" /></FormLabel>
                                        <Input value={formData.parent_asset_id || ''} onChange={(e) => handleChange('parent_asset_id', e.target.value)} placeholder="UUID of parent entity" />
                                        <Text fontSize="xs" color="gray.500" mt={1}>Link this component to a parent facility or wellfield cluster.</Text>
                                    </FormControl>

                                    <FormControl id="asset-field-qr_code_tag">
                                        <FormLabel fontSize="sm" fontWeight="bold">Barcode / QR Tag<AssetFieldHint field="qr_code_tag" /></FormLabel>
                                        <InputGroup>
                                            <InputLeftElement><MdQrCode color="gray" /></InputLeftElement>
                                            <Input value={formData.qr_code_tag || ''} onChange={(e) => handleChange('qr_code_tag', e.target.value)} placeholder="Physical tag ID for mobile scanning" />
                                        </InputGroup>
                                    </FormControl>

                                    <FormControl id="asset-field-model_url">
                                        <FormLabel fontSize="sm" fontWeight="bold">3D Digital Twin Model URL<AssetFieldHint field="model_url" /></FormLabel>
                                        <Input value={formData.model_url || ''} onChange={(e) => handleChange('model_url', e.target.value)} placeholder="https://... (.gltf or .glb format)" />
                                    </FormControl>

                                    {/* Structural Details for Bridge Assets */}
                                    {formData.type === 'Bridge' && (
                                        <Box p={4} bg={bridgeSectionBg} borderRadius="md" borderWidth="1px" borderColor={bridgeSectionBorder}>
                                            <Heading size="xs" mb={3} color="teal.500">Structural Engineering Data</Heading>
                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                <FormControl>
                                                    <FormLabel fontSize="xs">Number of Spans</FormLabel>
                                                    <Input 
                                                        size="sm" 
                                                        value={formData.metadata?.structural_details?.number_of_spans || ''} 
                                                        onChange={(e) => handleMetadataChange('structural_details', 'number_of_spans', e.target.value)} 
                                                        placeholder="e.g., 3"
                                                    />
                                                </FormControl>
                                                <FormControl>
                                                    <FormLabel fontSize="xs">Foundation Type</FormLabel>
                                                    <Input 
                                                        size="sm" 
                                                        value={formData.metadata?.structural_details?.foundation_type || ''} 
                                                        onChange={(e) => handleMetadataChange('structural_details', 'foundation_type', e.target.value)} 
                                                        placeholder="e.g., Concrete Piles"
                                                    />
                                                </FormControl>
                                            </SimpleGrid>
                                        </Box>
                                    )}
                                </VStack>
                            </TabPanel>

                        </TabPanels>
                    </Tabs>
                </ModalBody>

                {/* MODAL FOOTER */}
                <ModalFooter bg={bodyBg} borderTopWidth="1px" borderColor={footerBorder}>
                    {/* --- UPDATE */}
                    <Text fontSize="xs" color="gray.500" mr="auto">
                        AI readiness <b>{aiAnalysis.score}%</b> · {aiAnalysis.grade.label}
                    </Text>
                    {/* --- END UPDATE */}
                    <HStack spacing={3}>
                        <Button variant="ghost" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={handleSubmit}
                            isLoading={isSubmitting}
                            loadingText="Committing..."
                            leftIcon={<CheckIcon />}
                            fontFamily="mono"
                        >
                            {isEditing ? 'UPDATE ASSET RECORD' : 'COMMIT TO REGISTRY'}
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

AssetFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    asset: PropTypes.object,
    userRole: PropTypes.string
};

export default AssetFormModal;